import React from 'react';
import { motion } from 'framer-motion';
import { Globe, MapPin, TrendingUp, Users, Handshake, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
export function GlobalNetworkPage() {
  return <div className="w-full pt-20" data-id="element-210">
      {/* Hero */}
      <section className="bg-navy py-16 md:py-20" data-id="element-211">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-212">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-213">
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 md:mb-6" data-id="element-214">
              Global Network & Markets
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-215" />
            <p className="text-lg md:text-xl text-steel leading-relaxed max-w-2xl mx-auto" data-id="element-216">
              Connecting opportunities across international markets through
              trusted relationships and deep sector expertise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Global Advisory Reach */}
      <section className="bg-white py-16 md:py-20" data-id="element-217">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-218">
          <motion.div className="max-w-4xl mx-auto text-center mb-12 md:mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-219">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6" data-id="element-220">
              Global Advisory Reach
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-221" />
            <p className="text-base md:text-lg text-charcoal leading-relaxed" data-id="element-222">
              Strategic Metals Group Inc. operates at the intersection of global
              capital markets and the precious metals supply chain. Our
              international network spans investors, mining companies,
              operators, and commodity buyers across key markets worldwide.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8" data-id="element-223">
            {[{
            icon: Globe,
            title: 'International Presence',
            description: 'Active relationships across North America, Middle East, Europe, and Asia-Pacific regions.'
          }, {
            icon: Users,
            title: 'Diverse Network',
            description: 'Connections with institutional investors, family offices, mining operators, and commodity buyers.'
          }, {
            icon: TrendingUp,
            title: 'Market Intelligence',
            description: 'Real-time insights into precious metals markets, capital flows, and supply-demand dynamics.'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-224">
                <Card className="h-full border border-gold/20 hover:border-gold transition-all" data-id="element-225">
                  <CardContent className="p-5 md:p-6 text-center" data-id="element-226">
                    <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gold/10 rounded-full mb-4" data-id="element-227">
                      <item.icon className="h-7 w-7 md:h-8 md:w-8 text-gold" data-id="element-228" />
                    </div>
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-2 md:mb-3" data-id="element-229">
                      {item.title}
                    </h3>
                    <p className="text-charcoal leading-relaxed text-sm md:text-base" data-id="element-230">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Regional Focus */}
      <section className="bg-ivory py-16 md:py-20" data-id="element-231">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-232">
          <motion.div className="text-center mb-12 md:mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-233">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-234">
              Regional Expertise
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto" data-id="element-235" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-12" data-id="element-236">
            {[{
            region: 'Canada',
            icon: MapPin,
            description: 'Our Canada-based operations provide deep expertise in North American mining sectors, capital markets, and regulatory environments.',
            strengths: ['Mining sector expertise', 'Capital markets access', 'Regulatory knowledge', 'Institutional relationships']
          }, {
            region: 'Middle East',
            icon: MapPin,
            description: 'We have cultivated strategic relationships with buyers and investors across Middle Eastern markets for off-take agreements.',
            strengths: ['Buyer relationships', 'Off-take structuring', 'Cultural understanding', 'Market access']
          }, {
            region: 'International',
            icon: MapPin,
            description: 'Our extensive international network spans commodity trading hubs, investment centers, and mining jurisdictions worldwide.',
            strengths: ['Global network', 'Cross-border expertise', 'Market intelligence', 'Strategic partnerships']
          }].map((region, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} data-id="element-237">
                <Card className="h-full border border-gold/20" data-id="element-238">
                  <CardContent className="p-6 md:p-8" data-id="element-239">
                    <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gold rounded-full mb-4 md:mb-6" data-id="element-240">
                      <region.icon className="h-7 w-7 md:h-8 md:w-8 text-navy" data-id="element-241" />
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl font-bold text-navy mb-3 md:mb-4" data-id="element-242">
                      {region.region}
                    </h3>
                    <p className="text-charcoal leading-relaxed mb-4 md:mb-6 text-sm md:text-base" data-id="element-243">
                      {region.description}
                    </p>
                    <div className="space-y-2" data-id="element-244">
                      {region.strengths.map((strength, i) => <div key={i} className="flex items-center text-sm" data-id="element-245">
                          <span className="text-gold mr-2 flex-shrink-0" data-id="element-246">
                            •
                          </span>
                          <span className="text-charcoal" data-id="element-247">{strength}</span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Buyer & Investor Access */}
      <section className="bg-white py-16 md:py-20" data-id="element-248">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-249">
          <motion.div className="max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-250">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6 text-center" data-id="element-251">
              Buyer & Investor Access
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-8 md:mb-12" data-id="element-252" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-253">
              <div data-id="element-254">
                <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-3 md:mb-4 flex items-center" data-id="element-255">
                  <Users className="h-5 w-5 md:h-6 md:w-6 text-gold mr-2 flex-shrink-0" data-id="element-256" />
                  Investor Network
                </h3>
                <ul className="space-y-2 md:space-y-3 text-charcoal text-sm md:text-base" data-id="element-257">
                  {['Qualified institutional investors', 'Family offices seeking natural resources exposure', 'Private equity and venture capital firms', 'Strategic investors in mining and commodities', 'High-net-worth individuals'].map((item, i) => <li key={i} className="flex items-start" data-id="element-258">
                      <span className="text-gold mr-2 mt-0.5 flex-shrink-0" data-id="element-259">
                        •
                      </span>
                      <span data-id="element-260">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div data-id="element-261">
                <h3 className="font-heading text-lg md:text-xl font-semibold text-navy mb-3 md:mb-4 flex items-center" data-id="element-262">
                  <Handshake className="h-5 w-5 md:h-6 md:w-6 text-gold mr-2 flex-shrink-0" data-id="element-263" />
                  Buyer Relationships
                </h3>
                <ul className="space-y-2 md:space-y-3 text-charcoal text-sm md:text-base" data-id="element-264">
                  {['Commodity buyers in Middle Eastern markets', 'International trading houses', 'Precious metals refiners and processors', 'Strategic buyers seeking supply security', 'End-users across global markets'].map((item, i) => <li key={i} className="flex items-start" data-id="element-265">
                      <span className="text-gold mr-2 mt-0.5 flex-shrink-0" data-id="element-266">
                        •
                      </span>
                      <span data-id="element-267">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-Border Structuring */}
      <section className="bg-navy py-16 md:py-20" data-id="element-268">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-269">
          <motion.div className="max-w-4xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-270">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 md:mb-6 text-center" data-id="element-271">
              Cross-Border Opportunity Structuring
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-8 md:mb-12" data-id="element-272" />
            <div className="space-y-6" data-id="element-273">
              <p className="text-base md:text-lg text-steel leading-relaxed" data-id="element-274">
                International partnerships in the precious metals sector require
                sophisticated structuring to navigate regulatory environments,
                currency considerations, and cultural differences.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6" data-id="element-275">
                {[{
                title: 'Regulatory Navigation',
                description: 'Understanding and compliance with international mining, investment, and trade regulations.'
              }, {
                title: 'Currency & Risk Management',
                description: 'Structuring agreements that address currency fluctuations and geopolitical risks.'
              }, {
                title: 'Cultural Intelligence',
                description: 'Facilitating partnerships with sensitivity to cultural and business practices.'
              }, {
                title: 'Legal Frameworks',
                description: 'Coordinating with legal experts to ensure robust international agreements.'
              }].map((item, index) => <div key={index} className="bg-navy-light border-l-4 border-gold p-5 md:p-6 rounded" data-id="element-276">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-gold mb-2" data-id="element-277">
                      {item.title}
                    </h3>
                    <p className="text-steel text-sm md:text-base" data-id="element-278">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Supply Chain Insight */}
      <section className="bg-ivory py-16 md:py-20" data-id="element-279">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-280">
          <motion.div className="max-w-4xl mx-auto text-center" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-281">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4 md:mb-6" data-id="element-282">
              Precious Metals Supply Chain Insight
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gold mx-auto mb-6 md:mb-8" data-id="element-283" />
            <p className="text-base md:text-lg text-charcoal leading-relaxed mb-8 md:mb-12" data-id="element-284">
              Our global network provides unique visibility across the entire
              precious metals supply chain. This comprehensive perspective
              enables us to identify opportunities and structure partnerships
              that create value at every stage.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-285">
              {['Exploration', 'Development', 'Production', 'Distribution'].map((stage, index) => <motion.div key={index} className="bg-white border border-gold/20 hover:border-gold p-4 md:p-6 rounded-lg transition-all" initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} data-id="element-286">
                    <Target className="h-6 w-6 md:h-8 md:w-8 text-gold mx-auto mb-2 md:mb-3" data-id="element-287" />
                    <p className="font-heading text-sm sm:text-base md:text-lg font-semibold text-navy" data-id="element-288">
                      {stage}
                    </p>
                  </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}